import { KEYS } from '@/components/custom/editor/stringKeys/StringKeys'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'

import Loading from '@/components/custom/loading'
import {
    convertLexicalToDBJson,
    setJsonScriptEditor,
} from '@/features/screenplay/components/editor/utils/JsonUtils'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { $generateHtmlFromNodes } from '@lexical/html'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalEditor, EditorState } from 'lexical'
import { nanoid } from 'nanoid'
import { useEffect, useState } from 'react'
import { lexicalNodes } from './nodes/NodeCore'
import { NodeType } from './nodes/NodeType'
import SpAIWriterChatPlugin from './plugins/SpAIWriterChatPlugin'
import { SpAutoCompletePlugin } from './plugins/SpAutoCompletePlugin'
import { SpEditorInstancePlugin } from './plugins/SpEditorInstancePlugin'
import { SpEnterKeyPlugin } from './plugins/SpEnterKeyPlugin'
import SpNodeToolbarPlugin from './plugins/SpNodeToolbarPlugin'
import PrintPdfPlugin from './plugins/SpPrintPdfPlugin'
import { ShortcutKeyPlugin } from './plugins/SpShortcutKeyPlugin'
import { SpSuggestionMenuPlugin } from './plugins/SpSuggestionPlugin'
import { useSpEditorStore } from '../../stores/sp-editor.store'
import { useScreenplayEditor } from '../../hooks/useScreenplayEditor'
import { useScreenplayQuery, useUpdateScreenplayMutation } from '../../queries/screenplay.queries'
import { toastService } from '@/shared/services/toast.service'
import { useDebouncedCallback } from 'use-debounce'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { SerializedContent } from 'packages/shared/dist'

export const DEFAULT_CONTENT: SerializedContent[] = [
    {
        type: NodeType.Scene,
        children: [{ text: 'Ext Home - Day' }],
        id: nanoid(), version: 1
    },
]

export default function SpEditor() {
    const { project } = useWorkspace()
    const fileName = 'master'
    const store = useSpEditorStore()
    const { setHtmlString } = store
    const { editor } = useSceneStore()
    const { addCharacterToDb } = useScreenplayEditor() // Initialize editor hooks
    const [jsonScript, setJsonScript] = useState<any[] | undefined>(undefined)

    // Query for screenplay data
    const { data: screenplayEntity, isLoading } = useScreenplayQuery(project?.id, fileName)

    // Mutation for updating screenplay
    const updateScreenplay = useUpdateScreenplayMutation({
        onSuccess: () => {
            //toastService.success("Content saved successfully")
        },
        onError: (error: any) => {
            console.error('Failed to save content:', error)
            toastService.error("Failed to save content")
        }
    })

    const debouncedSave = useDebouncedCallback(async (editorState: EditorState, editor: LexicalEditor) => {
        if (!screenplayEntity) return
        const editorJson = editorState.toJSON()
        const data = convertLexicalToDBJson(editorJson)
        updateHTMLForPrintPDF(editor)

        updateScreenplay.mutate({
            ...screenplayEntity,
            content: data,
            updated_at: new Date()
        })
    }, 1000)

    useEffect(() => {
        if (screenplayEntity) {
            store.setScreenplayEntity(screenplayEntity)
            const saved = screenplayEntity.content ?? []
            try {
                if (saved && saved.length !== 0) {
                    if (Array.isArray(saved)) {
                        setJsonScript(saved)
                    } else {
                        console.warn('saved content is not in proper format', saved)
                        setJsonScript(DEFAULT_CONTENT)
                    }
                } else {
                    setJsonScript(DEFAULT_CONTENT)
                }
            } catch (error) {
                setJsonScript(DEFAULT_CONTENT)
            }
        } else if (!isLoading && !screenplayEntity) {
            // Handle case where screenplay doesn't exist yet? 
            // Ideally AddScreenPlay ensures it exists. 
            // For now default content
            setJsonScript(DEFAULT_CONTENT)
        }
    }, [screenplayEntity, isLoading])

    useEffect(() => {
        if (!editor) return
        updateHTMLForPrintPDF(editor)
    }, [editor])

    if (isLoading || !jsonScript)
        return (
            <div className="flex justify-center h-screen align-middle bg-white">
                <Loading />
            </div>
        )

    return (
        <LexicalComposer
            initialConfig={{
                namespace: 'MyEditor',
                nodes: [...lexicalNodes.map(([_type, node]) => node.kClass)],
                onError: (e) => {
                    throw e
                },
                editorState: (initialEditor: LexicalEditor) => {
                    if (!jsonScript) return
                    setJsonScriptEditor(initialEditor, jsonScript)
                },
            }}
        >
            <SpEditorInstancePlugin />
            <SpEnterKeyPlugin />
            <HistoryPlugin />

            <ShortcutKeyPlugin />
            <RichTextPlugin
                contentEditable={
                    <ContentEditable
                        //Width - 816(8.5in @96dpi) - 0.5 in for binding (total margin 0.5 + 1 -left + 1 in right)
                        className="flex flex-col outline-none items-center p-4 py-[1in] pb- w-[768px] px-[1in] bg-gray-50 min-h-screen"
                        id={KEYS.SP_CONTENT_EDITABLE}
                    />
                }
                placeholder={<div className="text-gray-400">Start typing...</div>}
                ErrorBoundary={LexicalErrorBoundary}
            />
            <OnChangePlugin
                ignoreSelectionChange={true}
                onChange={(editorState, editor, tags) => {
                    debouncedSave(editorState, editor)
                }}
            />
            <PrintPdfPlugin />
            {store.featuresEnabled.suggestionDropdown && <SpSuggestionMenuPlugin />}
            {store.featuresEnabled.aiCompletion && <SpAutoCompletePlugin />}
            {store.featuresEnabled.nodeToolbar && <SpNodeToolbarPlugin />}
            <SpAIWriterChatPlugin />
        </LexicalComposer>
    )

    function updateHTMLForPrintPDF(editor: LexicalEditor) {
        if (!editor) return
        const html = editor.read(() => {
            return $generateHtmlFromNodes(editor)
        })
        setHtmlString(html)
    }
}
